import {
  PermissionContext,
  permissionsOperations,
  resourcePermissionsOperations,
} from '@ardoq/access-control';
import { APIComponentAttributes, ArdoqId } from '@ardoq/api-types';
import { FeatureContext } from '@ardoq/features';

type ComponentCanDoArgs = {
  permissionContext: PermissionContext;
  component: Pick<
    APIComponentAttributes,
    '_id' | 'rootWorkspace' | 'model' | 'typeId'
  >;
  fieldId: ArdoqId;
  featureContext: FeatureContext;
};

export type ComponentCanDoInModesArgs = ComponentCanDoArgs & {
  isPresentationMode: boolean;
  isScenarioMode: boolean;
};

/* ---------------------------------------------------------------------------
   ---------------------------------------------------------------------------
   ----------------                                           ----------------
   ----------------             Fields Access Checks          ----------------
   ----------------                                           ----------------
   ---------------------------------------------------------------------------
   ------------------------------------------------------------------------ */

/**
 * Checks if the user can edit a field in a component.
 */
const canEditField = (args: ComponentCanDoArgs): boolean => {
  const { permissionContext, component } = args;
  return resourcePermissionsOperations.canEditWorkspace(
    permissionContext,
    component.rootWorkspace
  );
};

/**
 * Checks if the user can Read a field in a component. includes checking if the user is an org writer and if the workspace is editable.
 */
const canOnlyReadField = (args: ComponentCanDoArgs): boolean => {
  const { permissionContext, component } = args;
  const canEditWorkspace = resourcePermissionsOperations.canEditWorkspace(
    permissionContext,
    component.rootWorkspace
  );
  if (canEditWorkspace) {
    return false;
  }

  return resourcePermissionsOperations.canAccessWorkspace(
    permissionContext,
    component.rootWorkspace
  );
};

/* ---------------------------------------------------------------------------
   ---------------------------------------------------------------------------
   ----------------                                           ----------------
   ----------------           Components Access Checks        ----------------
   ----------------                                           ----------------
   ---------------------------------------------------------------------------
   ------------------------------------------------------------------------ */
/**
 * Checks if the user can access the component details
 */
const canAccessComponent = ({
  component,
  permissionContext,
}: Pick<ComponentCanDoArgs, 'component' | 'permissionContext'>): boolean => {
  return resourcePermissionsOperations.canAccessWorkspace(
    permissionContext,
    component.rootWorkspace
  );
};

/**
 * Checks if the user can edit a field in a component.
 */
const canEditComponent = ({
  component,
  permissionContext,
}: Pick<
  ComponentCanDoArgs,
  'component' | 'permissionContext' | 'featureContext'
>): boolean => {
  return resourcePermissionsOperations.canEditWorkspace(
    permissionContext,
    component.rootWorkspace
  );
};

/**
 * Checks if the user can unlock the component.
 */
const canUnlock = ({
  permissionContext,
  component,
}: {
  permissionContext: PermissionContext;
  component: Pick<APIComponentAttributes, 'lock'>;
}): boolean => {
  if (!permissionContext.user) return false;
  return (
    permissionContext.user._id === component.lock ||
    permissionsOperations.hasAdminRole(permissionContext)
  );
};

export const componentAccessControlOperation = {
  /**
   * Checks if the user can read a component. Includes checking if the user is
   * an org reader and if the workspace is accessible.
   */
  canAccessComponent,
  /**
   * Checks if the user can edit a component. Includes checking if the user is
   * an org writer and if the workspace is editable.
   */
  canEditComponent,

  /**
   * Checks if the user can edit a field in a component. Includes checking if
   * the user is an org writer and if the workspace is editable.
   */
  canEditField,

  /**
   * Checks if the user can Read a field in a component. Includes checking if
   * the user is an org writer and if the workspace is editable.
   */
  canOnlyReadField,

  /**
   * Checks if the user can unlock the component. Only the user who locked it
   * and org admins can.
   */
  canUnlock,
};
